import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import type { Dayjs } from 'dayjs';
import type { Task, TaskInstance } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import type HotelChannelPricingController from 'lumen/controllers/hotel/channel-pricing';
import type {
  Carate,
  Carecommendation,
  ChannelPricingSubscriptionConfig,
  Occupancy,
  Period,
  PredictedOccupancy,
  PriceAction,
  RestrictionRecommendation,
  RoomOccupancy,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { NB_OF_DAYS_SHOWN_ON_PAGE } from 'lumen/utils/constants/channel-pricing-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type ChannelPricingRouteParams = {
  fromDate: string;
};

interface RecommendationsAllRoomsRequestParams {
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  namespace: typeof APIGATEWAY;
  typeKey: string;
}

interface PricingPmsRoomsAllRequestParams {
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  namespace: typeof APIGATEWAY;
  typeKey: string;
}

interface PriceActionsRequestParams extends ExtractDateQueryParams {
  from_date_range_end: string;
  from_date_range_start: string;
  namespace: typeof APIGATEWAY;
  subscription_id: string;
}

interface RestrictionsRequestParams {
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  namespace: typeof APIGATEWAY;
  typeKey: string;
}

interface RestrictionsRecommendationsRequestParams {
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  namespace: typeof APIGATEWAY;
  typeKey: string;
}

interface RatesAllChannelsRequestParams {
  compare_extract_date: string;
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  namespace: typeof APIGATEWAY;
  typeKey: string;
}

type ExtractDateQueryParams = {
  [key in `compare${number}_extract_date` | 'compare_extract_date']?: string;
};

interface OccupancyRequestParams extends ExtractDateQueryParams {
  from_date_range_end: string;
  from_date_range_start: string;
  namespace: typeof APIGATEWAY;
  subscription_id: string;
}

interface ChannelPricingSubscriptionConfigParams {
  namespace: typeof APIGATEWAY;
  subscription_id: string;
  typeKey: string;
}

type ChannelPricingRouteModel = {
  occupancyDataTask: Task<
    void,
    [hotelId: string, rangeStart: string, rangeEnd: string]
  >;
  priceActionsDataRequestTask: Task<
    PriceAction[],
    [hotelId: string, rangeStart: string, rangeEnd: string]
  >;
  pricingPmsRoomsAllRequestTask: Task<
    Carate[] | null,
    [hotelId: string, rangeStart: string, rangeEnd: string]
  >;
  ratePreferenceTaskResult: TaskInstance<void>;
  ratesAllChannelsRequestTask: Task<
    Period[] | null,
    [hotelId: string, rangeStart: string, rangeEnd: string]
  >;
  recommendationsRequestTask: Task<
    Carecommendation[] | null,
    [hotelId: string, rangeStart: string, rangeEnd: string]
  >;
  requestChannelPricingSubscriptionConfigTask: Task<
    ChannelPricingSubscriptionConfig | null,
    [hotelId: string]
  >;
  requestRestrictionActionsOnPageLoadTask: Task<
    void,
    [hotelId: string, rangeStart: string, rangeEnd: string]
  >;
  restrictionsRecommendationsRequestTask: Task<
    RestrictionRecommendation[] | null,
    [hotelId: string, rangeStart: string, rangeEnd: string]
  >;
  restrictionsRequestTask: Task<
    Period[] | null,
    [hotelId: string, rangeStart: string, rangeEnd: string, compareDate: string]
  >;
};

export default class HotelChannelPricingRoute extends Route {
  queryParams = {
    fromDate: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service('channel-pricing/data-request')
  declare channelPricingDataRequest: Services['channel-pricing/data-request'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service('channel-pricing/liveupdate')
  declare liveupdate: Services['channel-pricing/liveupdate'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];
  @service('restrictions/action-poller')
  declare restrictionsActionPoller: Services['restrictions/action-poller'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  onClosingTheLoopLiveshopFinish = (data: {
    fromDate: string;
    toDate: string;
  }): void => {
    void this.channelPricingDataRequest.ratesAllChannelsRequestTask.perform(
      this.hotelId,
      data.fromDate,
      data.toDate,
    );
  };

  channelPricingSubscriptionConfigDataRequestTask = task(
    { restartable: true },
    async (hotelId: string) => {
      try {
        const queryParams: ChannelPricingSubscriptionConfigParams = {
          namespace: APIGATEWAY,
          subscription_id: hotelId,
          typeKey: 'channel-management/settings',
        };

        const subscriptionConfig =
          await this.store.queryRecord<ChannelPricingSubscriptionConfig>(
            'channel-pricing-subscription-config',
            { ...queryParams },
          );

        return subscriptionConfig;
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  occupancyDataRequestTask = task(
    { restartable: true },
    async (
      hotelId: string,
      rangeStart: string,
      rangeEnd: string,
    ): Promise<void> => {
      const requests = [];

      const rangeStartDayjs =
        this.hotelAndMarketService.toLocalTime(rangeStart);

      if (this.access.hasOTB) {
        const queryParams: OccupancyRequestParams = {
          namespace: APIGATEWAY,
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          subscription_id: hotelId,
        };

        // compare_extract_date, compare2_extract_date, etc.
        this.getChangeDates(rangeStartDayjs).forEach((changeDate, index) => {
          queryParams[`compare${index > 0 ? index + 1 : ''}_extract_date`] =
            changeDate;
        });

        requests.push(
          this.store.query<Occupancy>('occupancy', {
            ...queryParams,
            typeKey: 'revenueinsight/occupancies',
          }),
        );

        if (this.access.hasOTBForecastView) {
          requests.push(
            this.store.query<PredictedOccupancy>('predicted-occupancy', {
              ...queryParams,
              typeKey: 'revenueinsight/occupancies/predicted',
            }),
          );
        }

        if (this.access.hasRoomTypeAvailability) {
          requests.push(
            this.store.query<RoomOccupancy>('room-occupancy', {
              ...queryParams,
              typeKey: 'revenueinsight/occupancies/rooms',
            }),
          );
        }
      }

      try {
        // Occupancy & predicted occupancy should be present in the store. Can be accessed via period/days relationship
        await Promise.all(requests);
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  priceActionsDataRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      let apiResponse = null;

      const queryParams: PriceActionsRequestParams = {
        namespace: APIGATEWAY,
        from_date_range_start: rangeStart,
        from_date_range_end: rangeEnd,
        subscription_id: hotelId,
      };

      try {
        const response = await this.store.query<PriceAction>('price-action', {
          ...queryParams,
          typeKey: 'pricing-assistant/recommendations/action/status/latest',
        });

        apiResponse = [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );

  pricingPmsRoomsAllRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      let apiResponse = null;

      try {
        const requestParams: PricingPmsRoomsAllRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/rates/pms/rooms/all',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        apiResponse = await this.store.query<Carate>('carate', {
          ...requestParams,
        });

        return [...apiResponse];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  ratesAllChannelsRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      let apiResponse = null;

      const compareDate = this.hotelAndMarketService
        .getLocalTime()
        .subtract(1, 'day')
        .format('YYYY-MM-DD');

      try {
        const requestParams: RatesAllChannelsRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/rates/otas/all',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          compare_extract_date: compareDate,
          hotel_id: hotelId,
        };

        const response = await this.store.query<Period>('period', {
          ...requestParams,
        });
        apiResponse = [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );

  recommendationsForAllRoomsRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      let apiResponse = null;

      try {
        const requestParams: RecommendationsAllRoomsRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/recommendations/rooms/all',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const response = await this.store.query<Carecommendation>(
          'carecommendation',
          { ...requestParams },
        );
        apiResponse = [...response];

        // bit of a side-effect in this action, but we have the pricing recommendations response here
        // pass the recommendations response to the service so that it can see if there's a recommendation that requires polling
        this.pricingAssistantRecommendationActionPoller.restartPricingAssistantRecommendationActionIdPollers(
          apiResponse,
        );
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );

  requestRestrictionActionsOnPageLoadTask = task(
    { restartable: true },
    async (
      hotelId: string,
      fromDate: string,
      toDate: string,
    ): Promise<void> => {
      const restrictionActions =
        await this.restrictionsDataRequestService.restrictionActionRequestTask.perform(
          this.hotelId,
          fromDate,
          toDate,
        );

      if (macroCondition(!isTesting())) {
        this.restrictionsActionPoller.addActionsToFollow(restrictionActions);
      }
    },
  );

  restrictionsRecommendationsRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      if (!this.access.hasOTB) {
        return [];
      }

      let apiResponse = null;

      try {
        const requestParams: RestrictionsRecommendationsRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/restrictions/recommendations',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const response = await this.store.query<RestrictionRecommendation>(
          'restriction-recommendation',
          { ...requestParams },
        );
        apiResponse = [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );

  restrictionsRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      let apiResponse = null;

      try {
        const requestParams: RestrictionsRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/restrictions/aggregated',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const response = await this.store.query<Period>('period', {
          ...requestParams,
        });
        apiResponse = [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );

  get hotelId(): string {
    return this.hotelAndMarketService.hotel.id!;
  }

  activate(): void {
    void this.channelPricingSubscriptionConfigDataRequestTask.perform(
      this.hotelId,
    );

    if (this.access.hasSmartDistributionClosingTheLoopFeature) {
      this.liveupdate.subscribe(this.onClosingTheLoopLiveshopFinish);
    }
  }

  beforeModel(transition: Transition): void {
    if (!this.access.hasSmartDistributionTab) {
      this.transition.redirectToEnabledTab(transition);
    }

    this.transition.validateChannelPricingQueryParams(transition);

    // stop polling before executing the model hook - same setup as PriMa page
    this.pricingAssistantRecommendationActionPoller.stopPolling();
    if (this.access.hasSmartDistributionClosingTheLoopFeature) {
      void this.liveupdate.stopPolling();
      this.liveupdate.fromDate = null;
      this.liveupdate.toDate = null;
    }

    // Try out our update behavior and disable the original refresh
    // When this property is available, the service will bypass the original update behavior + call this function when the polling is finished
    this.pricingAssistantRecommendationActionPoller.skipAnimationTimer = true;
    this.pricingAssistantRecommendationActionPoller.channelPricingCallbackOnPushFinish =
      (
        hotelId: string,
        date: string,
      ): Promise<{ carecommendation: Carecommendation[] }> => {
        return this.channelPricingDataRequest.onChannelPricingPollingFinished(
          hotelId,
          date,
        );
      };
  }

  deactivate(): void {
    // cancel all related tasks when leaving the route
    void this.occupancyDataRequestTask.cancelAll();
    void this.recommendationsForAllRoomsRequestTask.cancelAll();
    void this.restrictionsRequestTask.cancelAll();
    void this.pricingPmsRoomsAllRequestTask.cancelAll();
    void this.ratesAllChannelsRequestTask.cancelAll();
    void this.channelPricingSubscriptionConfigDataRequestTask.cancelAll();
    void this.priceActionsDataRequestTask.cancelAll();

    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }
    // this.store.unloadAll('day');
    // this.store.unloadAll('period');
    this.store.unloadAll('carate');
    this.store.unloadAll('carecommendation');
    this.store.unloadAll('restriction');
    this.store.unloadAll('occupancy');
    this.store.unloadAll('predicted-occupancy');
    this.store.unloadAll('room-occupancy');
    this.store.unloadAll('restriction-recommendation');
    this.store.unloadAll('restriction-action');
    this.store.unloadAll('price-action');
    this.store.unloadAll('channel-pricing-subscription-config');
  }

  // Can this later be moved to a more shared place?
  getChangeDates(fromDate: Dayjs): string[] {
    const today = this.hotelAndMarketService.getLocalTime();
    const previousMonth = today.subtract(1, 'month');
    const nextMonth = today.add(1, 'month');
    const monthAfterNextMonth = today.add(2, 'month');

    if (
      fromDate.isSame(today, 'month') ||
      fromDate.isSame(previousMonth, 'month')
    ) {
      return [today.subtract(1, 'day').format('YYYY-MM-DD')];
    }
    if (
      fromDate.isSame(nextMonth, 'month') ||
      fromDate.isSame(monthAfterNextMonth, 'month')
    ) {
      return [
        today.subtract(1, 'day').format('YYYY-MM-DD'),
        today.subtract(7, 'day').format('YYYY-MM-DD'),
      ];
    }
    if (fromDate.isAfter(monthAfterNextMonth)) {
      return [today.subtract(7, 'day').format('YYYY-MM-DD')];
    }

    return [];
  }

  model(params: ChannelPricingRouteParams): ChannelPricingRouteModel {
    const fromDateObject = this.hotelAndMarketService
      .toLocalTime(params.fromDate)

      .startOf('week')
      .startOf('day');

    const fromDate = fromDateObject.format('YYYY-MM-DD');

    const toDate = fromDateObject
      .add(NB_OF_DAYS_SHOWN_ON_PAGE - 1, 'day')
      .format('YYYY-MM-DD');

    // Only the channel tab selection reads the preference, at render, so the
    // template shows a loading state off this instead of the route blocking.
    const ratePreferenceTaskResult =
      this.pricingAssistantDataRequestService.loadPaRatePreferenceTask.perform(
        this.hotelId,
      );

    const model: ChannelPricingRouteModel = {
      occupancyDataTask: this.occupancyDataRequestTask,
      ratePreferenceTaskResult,
      recommendationsRequestTask: this.recommendationsForAllRoomsRequestTask,
      pricingPmsRoomsAllRequestTask: this.pricingPmsRoomsAllRequestTask,
      restrictionsRequestTask: this.restrictionsRequestTask,
      restrictionsRecommendationsRequestTask:
        this.restrictionsRecommendationsRequestTask,
      ratesAllChannelsRequestTask: this.ratesAllChannelsRequestTask,
      requestRestrictionActionsOnPageLoadTask:
        this.requestRestrictionActionsOnPageLoadTask,
      requestChannelPricingSubscriptionConfigTask:
        this.channelPricingSubscriptionConfigDataRequestTask,
      priceActionsDataRequestTask: this.priceActionsDataRequestTask,
    };

    void this.requestRestrictionActionsOnPageLoadTask.perform(
      this.hotelId,
      fromDate,
      toDate,
    );

    // Perform relevant tasks in route
    void this.occupancyDataRequestTask.perform(this.hotelId, fromDate, toDate);

    void this.ratesAllChannelsRequestTask.perform(
      this.hotelId,
      fromDate,
      toDate,
    );

    void this.pricingPmsRoomsAllRequestTask.perform(
      this.hotelId,
      fromDate,
      toDate,
    );

    void this.restrictionsRequestTask.perform(this.hotelId, fromDate, toDate);

    void this.restrictionsRecommendationsRequestTask.perform(
      this.hotelId,
      fromDate,
      toDate,
    );

    void this.recommendationsForAllRoomsRequestTask.perform(
      this.hotelId,
      fromDate,
      toDate,
    );

    if (this.access.hasSmartDistributionClosingTheLoopFeature) {
      this.liveupdate.fromDate = fromDate;
      this.liveupdate.toDate = toDate;

      void this.priceActionsDataRequestTask.perform(
        this.hotelId,
        fromDate,
        toDate,
      );

      void this.liveupdate
        .fetchActiveChannelPricingLiveupdates()
        .then((result) => {
          if (result.length > 0) {
            this.liveupdate.startPolling(
              this.hotelId,
              result[0]!.bulkLiveupdateId,
            );
          }
        });
    }

    return model;
  }

  resetController(
    controller: HotelChannelPricingController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      // stop all polling when leaving the route
      this.pricingAssistantRecommendationActionPoller.stopPolling();
      this.restrictionsActionPoller.stopPolling();
      if (this.access.hasSmartDistributionClosingTheLoopFeature) {
        void this.liveupdate.stopPolling();
        this.liveupdate.unsubscribe(this.onClosingTheLoopLiveshopFinish);
        this.liveupdate.fromDate = null;
        this.liveupdate.toDate = null;
      }

      // Undo our Smart Distribution specific changes to the Pricing polling service
      this.pricingAssistantRecommendationActionPoller.channelPricingCallbackOnPushFinish =
        null;
      this.pricingAssistantRecommendationActionPoller.skipAnimationTimer = false;
    }
  }
}
